interface FilterState {
  region: string;
  year: string;
  sort: string;
}

interface CategoryFiltersProps {
  state: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

const regions = ["全部", "大陆", "港台", "日本", "欧美", "韩国"];
const years = ["全部", "1980-1989", "1990-1999", "2000-2009", "2010-2019", "2020-2029"];
const sorts = ["最新上映", "热门程度", "评分最高"];

export default function CategoryFilters({ state, onChange, onReset }: CategoryFiltersProps) {
  const isFiltered = state.region !== "全部" || state.year !== "全部" || state.sort !== "最新上映";

  const update = (patch: Partial<FilterState>) => {
    onChange({ ...state, ...patch });
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="hidden flex-col gap-3 p-4 md:flex">
        <div className="flex items-start gap-3">
          <span className="mt-1.5 w-10 shrink-0 text-xs text-foreground-500">地区</span>
          <div className="flex flex-1 flex-wrap gap-1.5">
            {regions.map((region) => (
              <button
                key={region}
                type="button"
                onClick={() => update({ region })}
                className={
                  state.region === region
                    ? "cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50"
                    : "cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-background-200"
                }
              >
                {region}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <span className="mt-1.5 w-10 shrink-0 text-xs text-foreground-500">年份</span>
          <div className="flex flex-1 flex-wrap gap-1.5">
            {years.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => update({ year })}
                className={
                  state.year === year
                    ? "cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50"
                    : "cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-background-200"
                }
              >
                {year === "全部" ? "全部年代" : year.replace("-", " — ")}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 border-t border-background-200 pt-3">
          <span className="w-10 shrink-0 text-xs text-foreground-500">排序</span>
          <div className="flex flex-1 flex-wrap gap-1.5">
            {sorts.map((sort) => (
              <button
                key={sort}
                type="button"
                onClick={() => update({ sort })}
                className={
                  state.sort === sort
                    ? "flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-secondary-200 px-2.5 py-1 text-xs text-secondary-950"
                    : "flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:bg-background-100"
                }
              >
                {sort === "最新上映" && <i className="ri-calendar-line" />}
                {sort === "热门程度" && <i className="ri-fire-line" />}
                {sort === "评分最高" && <i className="ri-star-line" />}
                {sort}
              </button>
            ))}
          </div>
          {isFiltered && (
            <button
              type="button"
              onClick={onReset}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-700"
            >
              <i className="ri-refresh-line" />
              重置
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 p-3 md:hidden">
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[10px] text-foreground-500">地区</span>
          <select
            value={state.region}
            onChange={(event) => update({ region: event.target.value })}
            className="w-full rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
          >
            {regions.map((region) => (
              <option key={region} value={region}>
                {region === "全部" ? "全部地区" : region}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[10px] text-foreground-500">年份</span>
          <select
            value={state.year}
            onChange={(event) => update({ year: event.target.value })}
            className="w-full rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year === "全部" ? "全部年代" : year}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[10px] text-foreground-500">排序</span>
          <select
            value={state.sort}
            onChange={(event) => update({ sort: event.target.value })}
            className="w-full rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
          >
            {sorts.map((sort) => (
              <option key={sort} value={sort}>
                {sort}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}